import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  GraduationCap,
  ArrowRight,
  Trash2,
  Pencil,
  Share2,
  Loader2,
  X as CloseIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import LessonView from "../components/LessonView";
import { toast } from "sonner";

/**
 * "שיעורי התורה שלי" — saved-lesson library.
 *
 * Lists every lesson the user has saved from the LessonBuilder. Each card
 * carries Open / Edit-title / Delete / Share actions. Opening a lesson
 * mounts <LessonView /> in a dialog without needing to re-generate it.
 */
export default function MyLessons() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const isEn = i18n.language === "en";
  const dateLocale = isEn ? "en-US" : "he-IL";
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = loading
  const [openLesson, setOpenLesson] = useState(null); // {id, title, lesson, meta}
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const reload = useCallback(async () => {
    try {
      const data = await api.listLessons();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
      toast.error(t("my_lessons.toast_load_failed"));
    }
  }, [t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?next=/beit-midrash/lessons", { replace: true });
      return;
    }
    reload();
  }, [user, authLoading, navigate, reload]);

  if (!user) return null;

  const open = async (id) => {
    try {
      const full = await api.getLesson(id);
      setOpenLesson(full);
    } catch {
      toast.error(t("my_lessons.toast_open_failed"));
    }
  };

  const saveTitle = async (id) => {
    const title = editTitle.trim();
    if (!title) {
      toast.error(t("my_lessons.toast_title_empty"));
      return;
    }
    try {
      await api.updateLesson(id, { title });
      toast.success(t("my_lessons.toast_title_saved"));
      setEditingId(null);
      reload();
    } catch {
      toast.error(t("my_lessons.toast_title_save_failed"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("my_lessons.confirm_delete"))) return;
    try {
      await api.delLesson(id);
      toast.success(t("my_lessons.toast_deleted"));
      reload();
    } catch {
      toast.error(t("my_lessons.toast_delete_failed"));
    }
  };

  const share = async (item) => {
    const text = `${item.title}\n\n${item.preview || ""}`.trim();
    try {
      if (navigator.share) {
        await navigator.share({ title: item.title, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("my_lessons.toast_link_copied"));
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div
      className="relative min-h-[calc(100vh-4rem)]"
      data-testid="my-lessons-page"
      dir={dir}
    >
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pt-8 pb-24">
        <div className="mb-8 flex items-center gap-3">
          <Link
            to="/beit-midrash"
            data-testid="my-lessons-back"
            aria-label={t("my_lessons.back_aria")}
            className="text-muted-foreground hover:text-gold transition-colors"
          >
            <ArrowRight className={`h-5 w-5 ${isEn ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </Link>
          <div>
            <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-gold">
              {t("my_lessons.eyebrow")}
            </p>
            <h1 className="font-heading text-3xl text-foreground flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-gold" strokeWidth={1.5} />
              {t("my_lessons.page_title")}
            </h1>
          </div>
        </div>

        {items === null && (
          <div className="text-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 mx-auto animate-spin text-gold" strokeWidth={1.5} />
            <p className="mt-2 text-sm font-heading">{t("my_lessons.loading")}</p>
          </div>
        )}

        {items !== null && items.length === 0 && (
          <div
            data-testid="my-lessons-empty"
            className="text-center text-sm text-muted-foreground border border-dashed border-border/60 rounded-sm px-4 py-12"
          >
            <p>{t("my_lessons.empty_line")}</p>
            <p className="mt-2">
              {t("my_lessons.empty_hint_prefix")}
              <Link
                to="/beit-midrash"
                className="text-gold hover:underline"
              >
                {t("my_lessons.empty_hint_link")}
              </Link>
              {t("my_lessons.empty_hint_suffix")}
            </p>
          </div>
        )}

        {items !== null && items.length > 0 && (
          <ul
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="my-lessons-list"
          >
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`my-lesson-${it.id}`}
                className="rounded-sm border border-border/60 bg-card p-5 flex flex-col gap-3 hover:border-gold/50 transition-colors"
              >
                {editingId === it.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      dir={dir}
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      data-testid={`my-lesson-edit-input-${it.id}`}
                      className="bg-background border-border/60 focus-visible:ring-gold"
                    />
                    <Button
                      size="sm"
                      onClick={() => saveTitle(it.id)}
                      data-testid={`my-lesson-edit-save-${it.id}`}
                      className="sacred-btn-gold !py-1.5 text-xs"
                    >
                      {t("my_lessons.save")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(null)}
                      data-testid={`my-lesson-edit-cancel-${it.id}`}
                      aria-label={t("my_lessons.cancel_aria")}
                    >
                      <CloseIcon className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => open(it.id)}
                    data-testid={`my-lesson-open-${it.id}`}
                    className="text-start group"
                  >
                    <h3 className="font-heading text-lg text-foreground group-hover:text-gold transition-colors">
                      {it.title}
                    </h3>
                  </button>
                )}

                {it.preview && (
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {it.preview}
                  </p>
                )}

                <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-border/40">
                  <span className="text-[11px] text-muted-foreground">
                    {t("my_lessons.sources_count_label", { count: it.sources_count })}
                    {new Date(it.updated_at).toLocaleDateString(dateLocale, {
                      dateStyle: "medium",
                    })}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => open(it.id)}
                      data-testid={`my-lesson-open-btn-${it.id}`}
                      className="text-xs h-8 px-2 text-muted-foreground hover:text-gold"
                    >
                      {t("my_lessons.open")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(it.id);
                        setEditTitle(it.title);
                      }}
                      data-testid={`my-lesson-edit-btn-${it.id}`}
                      aria-label={t("my_lessons.rename_aria")}
                      className="text-xs h-8 px-2 text-muted-foreground hover:text-gold"
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => share(it)}
                      data-testid={`my-lesson-share-btn-${it.id}`}
                      aria-label={t("my_lessons.share_aria")}
                      className="text-xs h-8 px-2 text-muted-foreground hover:text-gold"
                    >
                      <Share2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(it.id)}
                      data-testid={`my-lesson-delete-btn-${it.id}`}
                      aria-label={t("my_lessons.delete_aria")}
                      className="text-xs h-8 px-2 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!openLesson} onOpenChange={(v) => !v && setOpenLesson(null)}>
        <DialogContent
          dir={dir}
          className="max-w-3xl md:max-w-4xl max-h-[92vh] overflow-y-auto bg-background border border-gold/30"
          data-testid="my-lesson-dialog"
        >
          <DialogHeader className="text-start">
            <DialogTitle className="font-heading text-2xl text-foreground">
              {openLesson?.title}
            </DialogTitle>
          </DialogHeader>
          {openLesson && (
            <LessonView
              lesson={openLesson.lesson}
              meta={openLesson.meta}
              onClose={() => setOpenLesson(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
